import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Sparkles, Kanban, Globe2, Check, Zap, Target, ArrowRight, Building2, Mail, MessageCircleQuestion, Quote, Star } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1758874573672-e2731bd577e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHx5b3VuZyUyMHByb2Zlc3Npb25hbCUyMHJlbW90ZSUyMHdvcmslMjBlbmVyZ2V0aWN8ZW58MHx8fHwxNzg2NDY1NjQ3fDA&ixlib=rb-4.1.0&q=85";

const STORIES = [
  {
    quote: "The Match Score alone saved me hours. When I saw a 92% fit, I knew it was worth the effort — and the AI cover letter got me an interview within a week.",
    name: "Priya S.", role: "Senior Product Designer", outcome: "Landed offer at fully-remote fintech · 3 weeks",
    accent: "bg-[#22c55e]", initials: "PS",
  },
  {
    quote: "The Company Insights section flagged questions I never would've thought to ask. It made me sound like I'd worked there for a year.",
    name: "Marcus O.", role: "Staff Backend Engineer", outcome: "3 offers in one month · 40% raise",
    accent: "bg-[#eab308]", initials: "MO",
  },
  {
    quote: "I applied to 4 jobs a day using the tailored resume templates — the Executive one is unreal. Tracker + reminders kept me actually organized for once.",
    name: "Anaïs L.", role: "Head of Growth", outcome: "Hired remotely from Lisbon by a US Series B",
    accent: "bg-[#ec4899]", initials: "AL",
  },
];

export default function Landing() {
  return (
    <div data-testid="landing-page" className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Dots background, masked to fade out behind the hero copy so text stays readable */}
        <div
          className="absolute inset-0 bg-dots pointer-events-none"
          style={{
            WebkitMaskImage: "linear-gradient(to bottom, #000 0, #000 18%, transparent 30%, transparent 70%, #000 82%, #000 100%)",
            maskImage: "linear-gradient(to bottom, #000 0, #000 18%, transparent 30%, transparent 70%, #000 82%, #000 100%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7">
            <motion.span initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="nb-pill bg-[#eab308] mb-6">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> CAREER AI HUB · FREE MATCH SCORE · $19/MO UNLIMITED
            </motion.span>
            <motion.h1 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.05 }}
              className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95]">
              Your full-service <span data-testid="hero-career-concierge" className="bg-[#ec4899] px-3 rounded-lg inline-block -rotate-1 border-2 border-black text-white" style={{ color: '#fff', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 -1px 0 #000, 0 1px 0 #000, -1px 0 0 #000, 1px 0 0 #000' }}>career concierge</span>.
            </motion.h1>
            <motion.p initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}
              className="mt-6 text-lg md:text-xl text-gray-700 max-w-2xl">
              Upload your resume, paste a job description — get an instant <strong>Match Score</strong> and the exact keywords you're missing. Free forever. Pro users unlock the full AI kit for <strong>$19/mo</strong>: tailoring, 10+ PDF templates, company intel, interview prep, and a smart tracker with 7-day follow-up nudges.
            </motion.p>
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.15 }}
              className="mt-8 flex flex-wrap gap-3">
              <Link to="/jobs" data-testid="hero-browse-jobs" className="nb-btn bg-[#4338ca] text-white px-6 py-3 text-base inline-flex items-center gap-2">
                Browse jobs <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/signup" data-testid="hero-signup" className="nb-btn bg-white px-6 py-3 text-base">
                Get free Match Score
              </Link>
            </motion.div>
            <div className="mt-10 flex items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#22c55e]" strokeWidth={3} /> Free to start</div>
              <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#22c55e]" strokeWidth={3} /> No auto-renew</div>
              <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#22c55e]" strokeWidth={3} /> Cancel anytime</div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2 }}
              className="relative">
              <div className="relative border-2 border-black rounded-2xl overflow-hidden shadow-[8px_8px_0_0_#0A0A0A] bg-white">
                <img src={HERO_IMG} alt="remote professional" className="w-full h-[420px] object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 nb-card p-4 w-56 rotate-[-4deg] bg-[#22c55e] text-white">
                <div className="text-xs font-black tracking-widest uppercase">Match score</div>
                <div className="text-4xl font-black">92%</div>
                <div className="text-xs opacity-90">Senior Product Designer · Fully remote</div>
              </div>
              <div className="absolute -top-4 -right-4 nb-card p-3 rotate-[6deg] bg-[#eab308]">
                <div className="flex items-center gap-1.5 text-sm font-bold"><Zap className="w-4 h-4" /> Resume tailored in 8s</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature bento — 6 tiles now */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-16">
        <div className="mb-6 text-center">
          <div className="label-eyebrow">The Career AI Hub</div>
          <h2 className="text-3xl md:text-4xl font-black tracking-tighter">Everything a career coach does — for $19/mo.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Search, title: "Search + Free Match Score", desc: "Fresh listings, filter by tag & fit. Every job gets an instant Match Score based on your saved resume.", bg: "bg-white", tag: "Free" },
            { icon: Target, title: "Keyword Gap Analysis", desc: "See exactly which keywords the JD wants that your resume is missing — with placement hints.", bg: "bg-[#22c55e] text-white", tag: "Free · Hook" },
            { icon: Sparkles, title: "The Hub — AI tailoring", desc: "Unlimited resume + cover letter tailoring, aligned to the company's own values and voice.", bg: "bg-[#4338ca] text-white", tag: "Pro" },
            { icon: Mail, title: "The Design — 10+ PDF templates", desc: "Modern, Tech, Finance, Creative, Healthcare, Skilled Trades, Minimalist, Academic, Executive, Student.", bg: "bg-[#ec4899] text-white", tag: "Pro" },
            { icon: Building2, title: "The Intel — company research", desc: "Culture snapshot, values, tech stack. One click to Glassdoor + LinkedIn.", bg: "bg-white", tag: "Pro" },
            { icon: MessageCircleQuestion, title: "The Prep — questions & thank-yous", desc: "Predicted questions, STAR stories from your resume, and an AI Thank-You email from your interview notes.", bg: "bg-[#eab308]", tag: "Pro" },
          ].map((f, i) => (
            <motion.div key={i} initial={{ y: 24, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className={`nb-card p-6 relative ${f.bg}`}>
              <span className={`absolute top-4 right-4 nb-pill text-[10px] ${f.tag.startsWith("Free") ? "bg-black text-white" : "bg-white text-black"}`}>{f.tag}</span>
              <div className="w-11 h-11 rounded-lg border-2 border-black bg-white text-black flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <h3 className="text-xl font-bold pr-16">{f.title}</h3>
              <p className="mt-2 text-sm opacity-90 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Success Stories — Social Proof */}
      <section className="border-y-2 border-black bg-[#FEFCE8]" data-testid="success-stories">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <div>
              <div className="label-eyebrow">Success stories</div>
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter">Remoteful got them the offer.</h2>
            </div>
            <div className="flex items-center gap-1 text-sm text-gray-700">
              <div className="flex">{[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 text-[#eab308] fill-[#eab308]" />)}</div>
              <span className="ml-2 font-bold">4.9 average · 3,400+ hires helped</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STORIES.map((s, i) => (
              <motion.div key={i} initial={{ y: 20, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="nb-card p-6 flex flex-col bg-white">
                <Quote className="w-8 h-8 text-[#4338ca]" strokeWidth={2.5} />
                <p className="mt-3 text-gray-800 leading-relaxed">"{s.quote}"</p>
                <div className="mt-5 flex items-center gap-3 pt-4 border-t-2 border-dashed border-black">
                  <div className={`w-11 h-11 rounded-full border-2 border-black flex items-center justify-center font-black text-white ${s.accent}`}>{s.initials}</div>
                  <div className="min-w-0">
                    <div className="font-bold text-sm truncate">{s.name}</div>
                    <div className="text-xs text-gray-600 truncate">{s.role}</div>
                  </div>
                </div>
                <div className={`mt-3 nb-pill text-[11px] self-start ${s.accent} text-white`}>{s.outcome}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-[#4338ca] border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="label-eyebrow text-white/70">Ready when you are</div>
            <h2 className="text-3xl md:text-4xl font-black text-white mt-1">Stop scrolling. Start landing offers.</h2>
          </div>
          <div className="flex gap-3">
            <Link to="/signup" data-testid="cta-signup" className="nb-btn bg-white text-black px-6 py-3">Start free</Link>
            <Link to="/pricing" data-testid="cta-pricing" className="nb-btn bg-[#eab308] text-black px-6 py-3">See pricing</Link>
          </div>
        </div>
      </section>

      <footer className="border-t-2 border-black">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 flex flex-wrap items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2 font-bold"><Globe2 className="w-4 h-4" /> Career AI Hub — remote jobs everywhere.</div>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:underline">Terms</Link>
            <div className="text-gray-500">© {new Date().getFullYear()} Career AI Hub</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
